/*
 * Purpose: Bootstrap CareerOS private block + MCP slot in personal userspace only
 * Last modified: 2026-07-11 | Agent
 * Completeness: 95
 */
import type { AuthTenant } from '@/lib/auth-tenants';
import { tenantStorageKey } from '@/lib/tenantStorage';
import { loadWorkflowsFromStorage, persistWorkflows } from '@/lib/workflowUtils';
import type { Workflow } from '@/stores/workflowStore';
import { useConnectorLibraryStore } from '@/services/connectors/library/store';
import { useMcpStore } from '@/stores/mcpStore';
import {
  buildCareerOSPrivateConnector,
  CAREEROS_CONNECTOR_ID,
  CAREEROS_MCP_SERVER_ID,
  CAREEROS_WORKFLOW_SEED_KEY,
} from '@/seeds/careerosPrivateConnector';

const DEMO_TENANT_IDS = new Set(['tenant-personal', 'tenant-acme', 'tenant-ops']);

function slugFromEmail(email: string): string {
  const local = email.split('@')[0]?.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'workspace';
  return local.slice(0, 32);
}

/** True only for the signed-in user's personal workspace — not shared demo orgs. */
export function isPersonalUserspaceTenant(tenant: AuthTenant | null, email: string | null): boolean {
  if (!tenant || !email?.trim()) return false;
  if (DEMO_TENANT_IDS.has(tenant.id)) return false;
  const personalId = `tenant-${slugFromEmail(email)}`;
  return tenant.id === personalId && tenant.role === 'owner';
}

function bootstrapFlagKey(): string {
  return tenantStorageKey('careeros_userspace_bootstrapped');
}

function isBootstrapped(): boolean {
  return localStorage.getItem(bootstrapFlagKey()) === '1';
}

function markBootstrapped(): void {
  localStorage.setItem(bootstrapFlagKey(), '1');
}

function defaultMcpUrl(): string {
  return (import.meta.env.VITE_CAREEROS_MCP_URL as string | undefined)?.trim() || 'http://127.0.0.1:3301/mcp';
}

function buildCareerOSWorkflow(): Workflow {
  const now = new Date().toISOString();
  return {
    id: `wf-careeros-${CAREEROS_WORKFLOW_SEED_KEY}`,
    name: '📄 CareerOS — Tailored resume from JD',
    description:
      'Private userspace flow: paste a job description → CareerOS generates an ATS-optimized resume and returns the score.',
    nodes: [
      {
        id: 'trigger-manual',
        type: 'trigger',
        subtype: 'manual',
        config: {
          name: 'Job description input',
          description: 'Provide job_description, target_role, target_company in run payload',
        },
        x: 60,
        y: 140,
      },
      {
        id: 'careeros-resume',
        type: 'tool',
        subtype: 'mcp_tool',
        config: {
          name: 'Generate tailored resume',
          connectorId: CAREEROS_CONNECTOR_ID,
          mcpServerId: CAREEROS_MCP_SERVER_ID,
          mcpGoal: 'Generate ATS-optimized tailored resume from job description',
          blockIntent: 'careeros_generate_tailored_resume',
          toolName: 'careeros_generate_tailored_resume',
          arguments: JSON.stringify({
            job_description: '{{input.job_description}}',
            target_role: '{{input.target_role}}',
            target_company: '{{input.target_company}}',
            variant_key: '{{input.variant_key}}',
            theme_id: '{{input.theme_id}}',
            include_qr: false,
          }),
        },
        x: 340,
        y: 140,
      },
      {
        id: 'careeros-ats',
        type: 'tool',
        subtype: 'mcp_tool',
        config: {
          name: 'Validate ATS score',
          connectorId: CAREEROS_CONNECTOR_ID,
          mcpServerId: CAREEROS_MCP_SERVER_ID,
          mcpGoal: 'Confirm ATS keyword score meets 90+ target',
          blockIntent: 'careeros_validate_ats',
          toolName: 'careeros_validate_ats',
          arguments: JSON.stringify({
            variant_key: '{{input.variant_key}}',
            job_description: '{{input.job_description}}',
          }),
        },
        x: 620,
        y: 140,
      },
      {
        id: 'log-result',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log resume + ATS',
          message: 'CareerOS resume generated — check html_path and ats.score in prior step output',
        },
        x: 900,
        y: 140,
      },
    ],
    edges: [
      { id: 'e1', from: 'trigger-manual', to: 'careeros-resume' },
      { id: 'e2', from: 'careeros-resume', to: 'careeros-ats' },
      { id: 'e3', from: 'careeros-ats', to: 'log-result' },
    ],
    createdAt: now,
    updatedAt: now,
  };
}

function seedWorkflowIfMissing(): void {
  const workflows = loadWorkflowsFromStorage();
  const seedId = `wf-careeros-${CAREEROS_WORKFLOW_SEED_KEY}`;
  if (workflows.some((w) => w.id === seedId)) return;
  persistWorkflows([...workflows, buildCareerOSWorkflow()]);
}

/**
 * Register CareerOS connector + MCP server record in the active personal tenant.
 * Never writes to builtins or other tenants.
 */
export function bootstrapCareerOSUserspace(
  tenant: AuthTenant | null,
  email: string | null,
): boolean {
  if (!isPersonalUserspaceTenant(tenant, email)) return false;
  if (isBootstrapped()) return false;

  const lib = useConnectorLibraryStore.getState();
  if (!lib.getById(CAREEROS_CONNECTOR_ID)) {
    lib.register(buildCareerOSPrivateConnector());
  }

  useMcpStore.getState().upsert({
    id: CAREEROS_MCP_SERVER_ID,
    name: 'CareerOS',
  });

  seedWorkflowIfMissing();
  markBootstrapped();

  if (import.meta.env.DEV) {
    const url = defaultMcpUrl();
    console.info(
      `[CareerOS userspace] Registered ${CAREEROS_CONNECTOR_ID} + MCP slot "${CAREEROS_MCP_SERVER_ID}". ` +
        `Set vault secrets MCP_CAREEROS_URL (${url}) and MCP_CAREEROS_TOKEN, then Connect → MCP.`,
    );
  }

  return true;
}
